import React, { useContext } from "react";
import intl from "react-intl-universal";
import { mdiCheck, mdiClose } from "@mdi/js";

import commonUtils from "../../../../../../script/utils/commonUtils";
import attenuationUtils from "../../../../../../script/utils/attenuationUtils";

import { useInput } from "../../../../../hooks/useInput";
import { useOnChange } from "../../../../../hooks/useOnChange";
import { useValidation } from "../../../../../hooks/useValidation";

import { Grid } from "../../../../../components/Grid";
import { Spacer } from "../../../../../components/Spacer";
import { Button } from "../../../../../components/Button";
import { Dialog } from "../../../../../containers/Dialog";
import { DialogBody } from "../../../../../containers/DialogBody";
import { InputNumber } from "../../../../../components/InputNumber";
import { DialogFooter } from "../../../../../containers/DialogFooter";
import { SelectSingle } from "../../../../../components/SelectSingle";
import { ValidationContext } from "../../../../../contexts/ValidationContext";

export const AttenuationDialog = props => {
    const { calcArea } = props;

    const validationContext = useContext(ValidationContext);

    const attenuationClass = useInput("attenuationClass", props.attenuationClass);
    const thickness = useInput("thickness", props.thickness);
    const attenuation1 = useInput("attenuation1", calcArea.attenuation1);
    const attenuationFrequency1 = useInput("attenuationFrequency1", calcArea.attenuationFrequency1);
    const attenuation2 = useInput("attenuation2", calcArea.attenuation2);
    const attenuationFrequency2 = useInput("attenuationFrequency2", calcArea.attenuationFrequency2);

    const getPoints = calcArea => {
        const { originX, originY, originZ,
            uaxisX, uaxisY, uaxisZ,
            vaxisX, vaxisY, vaxisZ } = calcArea;
        const p1x = originX;
        const p1y = originY;
        const p2x = originX + uaxisX;
        const p2y = originY + uaxisY;
        const p3x = originX + vaxisX;
        const p3y = originY + vaxisY;

        let p1z = null, p2z = null, p3z = null;
        if (!commonUtils.isBlank(originZ)
            && !commonUtils.isBlank(uaxisZ)
            && !commonUtils.isBlank(vaxisZ)) {
            p1z = originZ;
            p2z = originZ + uaxisZ;
            p3z = originZ + vaxisZ;
        }
        return { p1x, p1y, p1z, p2x, p2y, p2z, p3x, p3y, p3z };
    };
    const wall = getPoints(calcArea);
    const p1x = useInput("p1x", wall.p1x);
    const p1y = useInput("p1y", wall.p1y);
    const p1z = useInput("p1z", wall.p1z);
    const p2x = useInput("p2x", wall.p2x);
    const p2y = useInput("p2y", wall.p2y);
    const p2z = useInput("p2z", wall.p2z);
    const p3x = useInput("p3x", wall.p3x);
    const p3y = useInput("p3y", wall.p3y);
    const p3z = useInput("p3z", wall.p3z);

    useValidation(value => !commonUtils.isBlank(value), [attenuation1]);
    useValidation(value => !commonUtils.isBlank(value), [attenuation2]);
    useValidation(value => !commonUtils.isBlank(value), [attenuationFrequency1]);
    useValidation(value => !commonUtils.isBlank(value), [attenuationFrequency2]);

    useOnChange((name, value, ignore) => {
        if (ignore) {
            return;
        }
        const found = attenuationUtils.getThickness(attenuationClass.value, thickness.value);
        if (found === null) {
            thickness.set(null, true);
            attenuation1.set(null, true);
            attenuationFrequency1.set(null, true);
            attenuation2.set(null, true);
            attenuationFrequency2.set(null, true);
        } else {
            attenuation1.set(found.attenuation1, true);
            attenuationFrequency1.set(found.attenuationFrequency1, true);
            attenuation2.set(found.attenuation2, true);
            attenuationFrequency2.set(found.attenuationFrequency2, true);
        }
    }, [thickness, attenuationClass]);

    useOnChange((name, value, ignore) => {
        if (ignore) {
            return;
        }
        const att = attenuationUtils.getAttenuation(attenuation1.value, attenuation2.value,
            attenuationFrequency1.value, attenuationFrequency2.value);
        if (att) {
            attenuationClass.set(att.attenuationClass);
            thickness.set(att.thickness);
        } else {
            attenuationClass.set(null, true);
            thickness.set(null, true);
        }
    }, [attenuation1, attenuation2, attenuationFrequency1, attenuationFrequency2]);

    const onConfirm = async () => {
        const allValid = await validationContext.allValid();
        if (!allValid) {
            return;
        }
        const customAttenuation = (
            attenuationClass.value === null
            && attenuation1.value !== null
            && attenuation2.value !== null
        );
        if (customAttenuation) {
            calcArea.attenuationClass = "Custom";
        } else {
            calcArea.attenuationClass = attenuationClass.value;
        }
        calcArea.thickness = thickness.value;
        calcArea.attenuation1 = attenuation1.value;
        calcArea.attenuationFrequency1 = attenuationFrequency1.value;
        calcArea.attenuation2 = attenuation2.value;
        calcArea.attenuationFrequency2 = attenuationFrequency2.value;
        props.onConfirm();
    };

    return (
        <Dialog
            width="400px"
            closeIcon={false}
            title={intl.get("entry.plan.areas.attenuation")}
            onClose={() => props.onClose()}>
            <DialogBody>
                <Grid columns="2">
                    <SelectSingle {...attenuationClass.bind}
                        title={intl.get("entry.plan.areas.attenuationClass")}
                        toLabel={val => (val ? intl.get(`entry.plan.areas.attenuationClass${val}`) : null)}>
                        {attenuationUtils.getAttenuationClasses()
                            .map(attenuationClass =>
                                <option key={attenuationClass} value={attenuationClass}>
                                    {attenuationClass ? intl.get(`entry.plan.areas.attenuationClass${attenuationClass}`) : null}
                                </option>
                            )
                        }
                    </SelectSingle>
                    <SelectSingle {...thickness.bind} title={intl.get("entry.plan.areas.thickness")}
                        toLabel={value => (value ? String(value).replace(".", ",") : null)}>
                        {attenuationUtils.getThicknesses(attenuationClass.value)
                            .map(thickness =>
                                <option key={thickness} value={thickness}>
                                    {thickness}
                                </option>
                            )
                        }
                    </SelectSingle>
                </Grid>
                <Spacer variant="horizontal" />
                <Grid columns="3">
                    <InputNumber {...p1x.bind}
                        title={intl.get("entry.plan.areas.p1x")}
                        fractionDigits={2}
                        disabled={true} />
                    <InputNumber {...p2x.bind}
                        title={intl.get("entry.plan.areas.p2x")}
                        fractionDigits={2}
                        disabled={true} />
                    <InputNumber {...p3x.bind}
                        title={intl.get("entry.plan.areas.p3x")}
                        fractionDigits={2}
                        disabled={true} />
                    <InputNumber {...p1y.bind}
                        title={intl.get("entry.plan.areas.p1y")}
                        fractionDigits={2}
                        disabled={true} />
                    <InputNumber {...p2y.bind}
                        title={intl.get("entry.plan.areas.p2y")}
                        fractionDigits={2}
                        disabled={true} />
                    <InputNumber {...p3y.bind}
                        title={intl.get("entry.plan.areas.p3y")}
                        fractionDigits={2}
                        disabled={true} />
                    <InputNumber {...p1z.bind}
                        title={intl.get("entry.plan.areas.p1z")}
                        fractionDigits={2}
                        disabled={true} />
                    <InputNumber {...p2z.bind}
                        title={intl.get("entry.plan.areas.p2z")}
                        fractionDigits={2}
                        disabled={true} />
                    <InputNumber {...p3z.bind}
                        title={intl.get("entry.plan.areas.p3z")}
                        fractionDigits={2}
                        disabled={true} />
                </Grid>
                <Spacer variant="horizontal" />
                <Grid columns="2">
                    <InputNumber {...attenuation1.bind}
                        title={intl.get("entry.plan.areas.attenuation1")}
                        fractionDigits={2} />
                    <InputNumber {...attenuationFrequency1.bind}
                        title={intl.get("entry.plan.areas.attenuationFrequency1")}
                        fractionDigits={2} />
                    <InputNumber {...attenuation2.bind}
                        title={intl.get("entry.plan.areas.attenuation2")}
                        fractionDigits={2} />
                    <InputNumber {...attenuationFrequency2.bind}
                        title={intl.get("entry.plan.areas.attenuationFrequency2")}
                        fractionDigits={2} />
                </Grid>
            </DialogBody>
            <DialogFooter>
                <Button
                    svgIcon={mdiCheck}
                    value={intl.get("common.ok")}
                    onClick={onConfirm} />
                <Spacer size="small" variant="vertical" />
                <Button
                    svgIcon={mdiClose}
                    onClick={() => props.onClose()}
                    value={intl.get("common.cancel")} />
            </DialogFooter>
        </Dialog>
    );
};
